import Flex from '@react-css/flex';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

import { SettingsSectionTitle } from 'containers/SettingsPage/SettingsLayout';
import useAlertQueue from 'hooks/useAlertQueue';
import { useAppSelector } from 'store';
import { selectCompany } from 'store/selectors/me/company';
import Accordion from 'storybookConfig/stories/cells/Accordion';
import Card from 'storybookConfig/stories/cells/Card';
import Modal from 'storybookConfig/stories/cells/Modal';
import Body from 'storybookConfig/stories/molecules/Body';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import SecondaryButton from 'storybookConfig/stories/molecules/Button/SecondaryButton';
import Checkbox from 'storybookConfig/stories/molecules/Checkbox';
import Chip from 'storybookConfig/stories/molecules/Chip';
import Heading from 'storybookConfig/stories/molecules/Heading';
import TextAreaInput from 'storybookConfig/stories/molecules/Input/TextAreaInput';
import Label from 'storybookConfig/stories/molecules/Label';
import Select from 'storybookConfig/stories/molecules/Select';
import type { ReactSelectOption } from 'types/general';
import { CommerceType } from 'types/models/company';
import type ApiError from 'utils/ApiError';
import {
  cleanupPartnership,
  clearAndResyncImages,
  deleteCompanyStripe,
  ensureMappings,
  generateInvoices,
  rebuildSearchIndex,
  reinitPartnerships,
  resetMappingImages,
  resetMappings,
  resyncPartnershipMappings,
  resyncVariantMappings,
  setDefaultEDISchemaLive,
  testShopifySecret,
} from 'utils/api/admin';
import { getPartners } from 'utils/api/partners';
import { getProductSearch } from 'utils/api/products';
import { supportEDIDocumentNumbers } from 'utils/edi';

interface AdminAction {
  label: string;
  description: string;
  run: () => Promise<unknown>;
  isDisabled?: boolean;
  needsConfirm?: boolean;
  isDestructive?: boolean;
}

const useSearchOptions = (
  key: string,
  search: string,
  fetchOptions: (search: string) => Promise<ReactSelectOption[]>
) => {
  const debouncedSearch = useDebounce(search, 300);
  return useQuery({
    queryKey: ['adminActions', key, debouncedSearch],
    queryFn: () => fetchOptions(debouncedSearch),
    enabled: debouncedSearch !== '',
  });
};

interface PartnershipSelectProps {
  isBuyer: boolean;
  label: string;
  value: ReactSelectOption | null;
  onChange: (option: ReactSelectOption | null) => void;
}

const PartnershipSelect = ({ isBuyer, label, value, onChange }: PartnershipSelectProps) => {
  const inputId = useId();
  const [search, setSearch] = useState('');
  const options = useSearchOptions('partners', search, (term) =>
    getPartners(isBuyer ? { sellerName: term } : { buyerName: term }).then(({ data }) =>
      data.map((p) => ({ value: p._id, label: isBuyer ? p.sellerName : p.buyerName }))
    )
  );

  return (
    <Flex column gap="4px">
      <Label htmlFor={inputId}>{label}</Label>
      <Select
        inputId={inputId}
        isCreatable
        isClearable
        isFullWidth
        filterOption={null}
        placeholder="Search by partner name or paste an ID"
        options={options.data ?? []}
        isLoading={options.isFetching}
        value={value}
        onChange={(option) => onChange(option as ReactSelectOption | null)}
        onInputChange={setSearch}
      />
    </Flex>
  );
};

interface ProductsSelectProps {
  value: readonly ReactSelectOption[];
  onChange: (options: readonly ReactSelectOption[]) => void;
}

const ProductsSelect = ({ value, onChange }: ProductsSelectProps) => {
  const inputId = useId();
  const [search, setSearch] = useState('');
  const options = useSearchOptions('products', search, (term) =>
    getProductSearch({ query: term }).then(({ data }) =>
      (data.products ?? []).map((p) => ({ value: p.id, label: p.title }))
    )
  );

  return (
    <Flex column gap="4px">
      <Label htmlFor={inputId}>Products (optional)</Label>
      <Select
        inputId={inputId}
        isMulti
        isCreatable
        isFullWidth
        filterOption={null}
        placeholder="Search by title or paste IDs"
        options={options.data ?? []}
        isLoading={options.isFetching}
        value={value}
        onChange={(selected) => onChange(selected as readonly ReactSelectOption[])}
        onInputChange={setSearch}
      />
    </Flex>
  );
};

interface SectionProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

const Section = ({ title, description, children }: SectionProps) => (
  <section aria-label={title}>
    <Card padding="16px 24px" gap="12px">
      <Flex column gap="4px">
        <Heading variant="Headings/H4">{title}</Heading>
        <Body variant="Body/Body Small">{description}</Body>
      </Flex>
      {children}
    </Card>
  </section>
);

const AdminActionsPanel = () => {
  const company = useAppSelector(selectCompany);
  const alertQueue = useAlertQueue();
  const companyId = company?._id ?? '';
  const isBuyer = company?.commerceType === CommerceType.Buyer;

  const [ediDocument, setEdiDocument] = useState<ReactSelectOption<number> | null>(null);
  const [partnership, setPartnership] = useState<ReactSelectOption | null>(null);
  const [mappingPartnership, setMappingPartnership] = useState<ReactSelectOption | null>(null);
  const [products, setProducts] = useState<readonly ReactSelectOption[]>([]);
  const [orderIds, setOrderIds] = useState('');
  const [settledExternally, setSettledExternally] = useState(false);
  const [pendingAction, setPendingAction] = useState<AdminAction>();

  const running = useMutation({
    mutationFn: (action: AdminAction) => action.run(),
    onSuccess: (response, action) => {
      const data = (response as { data?: unknown } | undefined)?.data;
      const hasData = data && Object.keys(data).length > 0;
      alertQueue.addSuccessAlert(
        `${action.label} done`,
        hasData ? JSON.stringify(data) : 'Accepted. Background jobs may still be running.'
      );
    },
    onError: (error: ApiError, action) => alertQueue.addErrorAlert(action.label, error.message),
  });

  if (!company) {
    return <Body>Log in to a company to use these actions.</Body>;
  }

  const renderButtons = (actions: AdminAction[]) => (
    <Flex gap="8px" flexWrap="wrap">
      {actions.map((action) => (
        <SecondaryButton
          key={action.label}
          size="small"
          kind={action.isDestructive ? 'destructive' : 'action'}
          title={action.description}
          disabled={action.isDisabled || running.isLoading}
          onClick={() => (action.needsConfirm ? setPendingAction(action) : running.mutate(action))}
        >
          {action.label}
        </SecondaryButton>
      ))}
    </Flex>
  );

  // Backend falls back to every mapping of the buyer when no scope is given.
  const isWholeCompanyScope = !mappingPartnership && products.length === 0;
  let mappingScope = `Scope: all mappings of ${company.name}.`;
  if (mappingPartnership) mappingScope = `Scope: partnership ${mappingPartnership.label}.`;
  else if (products.length > 0) mappingScope = `Scope: ${products.length} selected product(s).`;
  const mappingConditions = () => ({
    buyerCompanyObjectID: companyId,
    ...(mappingPartnership && { partnershipID: mappingPartnership.value }),
    ...(products.length > 0 && { productIDs: products.map((p) => p.value) }),
  });
  const mappingAction = (
    label: string,
    description: string,
    run: (conditions: ReturnType<typeof mappingConditions>) => Promise<unknown>,
    isDestructive = false
  ): AdminAction => ({
    label,
    description: `${description} ${mappingScope}`,
    run: () => run(mappingConditions()),
    needsConfirm: isDestructive || isWholeCompanyScope,
    isDestructive,
  });

  const parsedOrderIds = orderIds.split(/[\s,]+/).filter(Boolean);

  return (
    <Flex column gap="16px">
      <Card padding="16px 24px" gap="4px">
        <Body variant="Body/Body Small">Acting on</Body>
        <Flex alignItemsCenter gap="8px" flexWrap="wrap">
          <Body variant="Body/Regular Bold">{company.name}</Body>
          <Chip size="small">{isBuyer ? 'Retailer' : 'Supplier'}</Chip>
          <Body variant="Body/Body Small">{company._id}</Body>
        </Flex>
      </Card>

      <Section title="Company" description="Runs against the company above. No input needed.">
        {renderButtons([
          {
            label: 'Resync variant mappings',
            description: 'Re-syncs variant data for every variant mapping of this company.',
            run: () => resyncVariantMappings(companyId),
          },
          {
            label: 'Rebuild search index',
            description: "Reindexes this company's search documents.",
            run: () => rebuildSearchIndex(companyId),
          },
          {
            label: 'Test Shopify secret',
            description: "Checks this company's Shopify app secret.",
            run: () => testShopifySecret(companyId),
          },
          {
            label: 'Re-init partnerships',
            description: "Re-initializes this company's partnerships.",
            run: () => reinitPartnerships(companyId),
          },
          {
            label: 'Delete Stripe',
            description: "Deletes this company's Stripe customer, account and payment method.",
            run: () => deleteCompanyStripe(companyId),
            needsConfirm: true,
            isDestructive: true,
          },
        ])}
      </Section>

      <Section title="EDI" description="Use the default mappings for one EDI document.">
        <Flex alignItemsEnd gap="8px" flexWrap="wrap">
          <Flex.Item grow={1}>
            <Flex column gap="4px">
              <Label htmlFor="admin-edi-document">EDI document</Label>
              <Select
                inputId="admin-edi-document"
                isFullWidth
                options={supportEDIDocumentNumbers(isBuyer).map((doc) => ({
                  value: doc.number,
                  label: `${doc.number} · ${doc.name}`,
                }))}
                value={ediDocument}
                onChange={setEdiDocument}
              />
            </Flex>
          </Flex.Item>
          {renderButtons([
            {
              label: 'Use default EDI mappings',
              description: 'Sets the default schema live for the selected EDI document.',
              run: () => setDefaultEDISchemaLive(ediDocument!.value),
              isDisabled: !ediDocument,
            },
          ])}
        </Flex>
      </Section>

      <Section title="Partnership" description="Actions on one partnership of this company.">
        <PartnershipSelect
          isBuyer={isBuyer}
          label="Partnership"
          value={partnership}
          onChange={setPartnership}
        />
        {renderButtons([
          {
            label: 'Resync partnership mappings',
            description: 'Re-selects every mapping of the selected partnership.',
            run: () => resyncPartnershipMappings(partnership!.value),
            isDisabled: !partnership,
          },
          {
            label: 'Cleanup partnership',
            description: 'Cleans up the selected partnership.',
            run: () => cleanupPartnership(partnership!.value),
            isDisabled: !partnership,
            needsConfirm: true,
            isDestructive: true,
          },
        ])}
      </Section>

      {isBuyer && (
        <Section
          title="Catalog mappings"
          description="Pick a partnership or products to narrow the scope. Partnership wins if both are set."
        >
          <PartnershipSelect
            isBuyer={isBuyer}
            label="Partnership (optional)"
            value={mappingPartnership}
            onChange={setMappingPartnership}
          />
          <ProductsSelect value={products} onChange={setProducts} />
          <Body variant="Body/Body Small Bold">{mappingScope}</Body>
          {renderButtons([
            mappingAction('Reset mappings', 'Resets broken mappings.', resetMappings),
            mappingAction(
              'Reset mapping images',
              'Resets synced image data on mappings.',
              resetMappingImages
            ),
            mappingAction('Ensure mappings', 'Creates mappings that are missing.', ensureMappings),
            mappingAction(
              'Clear and resync images',
              "Deletes product images in the retailer's platform and resyncs them.",
              clearAndResyncImages,
              true
            ),
          ])}
        </Section>
      )}

      <Section
        title="Invoices"
        description="Manually invoice specific orders, skipping the normal lookback window. Order IDs are global, not tied to the company above."
      >
        <Flex column gap="4px">
          <Label htmlFor="admin-order-ids">Order IDs</Label>
          <TextAreaInput
            as="textarea"
            id="admin-order-ids"
            placeholder="1-100 IDs, comma or newline separated"
            value={orderIds}
            onChange={(e) => setOrderIds(e.currentTarget.value)}
          />
        </Flex>
        <Checkbox
          name="settledExternally"
          checked={settledExternally}
          onChange={(e) => setSettledExternally(e.currentTarget.checked)}
        >
          Settled externally (create as already paid, never charged)
        </Checkbox>
        {renderButtons([
          {
            label: 'Generate invoices',
            description: 'Invoices the listed orders.',
            run: () => generateInvoices({ orderIds: parsedOrderIds, settledExternally }),
            isDisabled: parsedOrderIds.length === 0,
          },
        ])}
      </Section>

      <Modal.Root
        show={!!pendingAction}
        centered
        animation={false}
        aria-labelledby="admin-action-confirm-title"
        onHide={() => setPendingAction(undefined)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <span id="admin-action-confirm-title">{pendingAction?.label}?</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingAction?.description} This runs against {company.name}.
        </Modal.Body>
        <Modal.Footer>
          <SecondaryButton size="small" onClick={() => setPendingAction(undefined)}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            size="small"
            onClick={() => {
              if (pendingAction) running.mutate(pendingAction);
              setPendingAction(undefined);
            }}
          >
            Confirm
          </PrimaryButton>
        </Modal.Footer>
      </Modal.Root>
    </Flex>
  );
};

// Collapsed by default; nothing mounts or fetches until opened.
const AdminActions = () => (
  <>
    <SettingsSectionTitle>Admin actions</SettingsSectionTitle>
    <Accordion.Root>
      <Accordion.Head text="Advanced admin actions" />
      <Accordion.Body>
        <AdminActionsPanel />
      </Accordion.Body>
    </Accordion.Root>
  </>
);

export default AdminActions;
